import { Directive, ElementRef, OnInit, OnDestroy, Self } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NgControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AudioService } from '../../../services/audio.service';

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[appAudioReachability]',
  standalone: true,
})
export class AudioReachabilityDirective implements OnInit, OnDestroy {
  private subscription: Subscription | undefined;

  constructor(
    private el: ElementRef,
    private http: HttpClient,
    @Self() private control: NgControl,
    private audioService: AudioService
  ) {}

  ngOnInit() {
    // @ts-ignore
    this.subscription = this.control.valueChanges.subscribe(newValue => {
      this.checkAudioReachability(newValue);
    });
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  async checkAudioReachability(url: string) {
    // Remove previously set class if any
    this.el.nativeElement.classList.remove('unreachable-audio');

    // Check reachability and validate audio file type
    if (url) {
      try {
        // Check if URL points to a valid audio file type
        const audioExtensions = ['.mp3', '.wav', '.ogg', '.m4a', '.aac', '.flac', '.wma'];
        const isAudioFile = audioExtensions.some(ext =>
          url.toLowerCase().includes(ext) || url.toLowerCase().endsWith(ext)
        );

        if (!isAudioFile) {
          // If not an audio file, add invalid class and stop any related audio
          this.el.nativeElement.classList.add('unreachable-audio');
          this.stopInvalidAudio(url);
          return;
        }

        // Test if the audio URL is reachable using the proxy endpoint
        const proxiedUrl = this.getProxiedAudioUrl(url);
        await this.http.head(proxiedUrl).toPromise();
      } catch (error) {
        // If an error occurs (audio is not reachable or invalid), add the class and stop any related audio
        this.el.nativeElement.classList.add('unreachable-audio');
        this.stopInvalidAudio(url);
      }
    }
  }

  /**
   * Stop any currently playing audio when URL becomes invalid
   */
  private stopInvalidAudio(invalidUrl: string): void {
    // Stop map background sound if it matches the invalid URL
    if (this.audioService.isMapAudioPlaying()) {
      this.audioService.stopMapBackgroundSound();
    }

    // Stop all element audio - we don't have easy access to which specific element
    // this directive is validating, so we stop all element audio to be safe
    // This matches the behavior when focus is cleared
    this.audioService.handleElementFocusCleared();
  }

  /**
   * Get proxied audio URL to avoid CORS issues with external audio files
   */
  private getProxiedAudioUrl(soundUrl: string): string {
    // Check if it's already a proxied URL or a relative URL
    if (soundUrl.includes('/api/public/audio') || !soundUrl.startsWith('http')) {
      return soundUrl;
    }

    // Use the audio proxy endpoint for external URLs
    return `${environment.apiUrl}/public/audio?url=${encodeURIComponent(soundUrl)}`;
  }
}
